import { createSlice, PayloadAction } from '@reduxjs/toolkit';
// Import Types
import type { UserInfo, UserSettings } from 'types';
// Import Store
import { doLogout } from './auth';
// Import Constants
import { Appearance, ViewMode } from 'constants/common';

export interface UserDataSliceState {
    isLoading: boolean;
    errorMessage: Nilable<string>;
    data: Nullable<UserInfo>;
    language: Nullable<string>;
    applicationModes: {
        ViewMode: UserSettings['ViewMode'];
        Appearance: UserSettings['Appearance'];
    };
}

const initialState: UserDataSliceState = {
    isLoading: true,
    errorMessage: null,
    data: null,
    language: null,
    applicationModes: {
        ViewMode: ViewMode.Normal,
        Appearance: Appearance.Light,
    },
};

export const userDataSlice = createSlice({
    name: 'userData',
    initialState,
    reducers: (create) => ({
        setUserDataFetch: create.reducer((state) => {
            state.isLoading = true;
        }),

        setUserData: create.reducer((state, { payload }: PayloadAction<UserInfo>) => {
            state.isLoading = false;
            state.errorMessage = null;

            state.data = payload;
            state.language = payload.Settings.Language ?? null;
            state.applicationModes = {
                ViewMode: payload.Settings.ViewMode,
                Appearance: payload.Settings.Appearance,
            };
        }),

        setUserDataFailed: create.reducer((state, { payload }: PayloadAction<Nilable<string>>) => {
            state.isLoading = false;
            state.errorMessage = payload ?? null;
        }),

        setUserDataLanguage: create.reducer((state, { payload }: PayloadAction<UserSettings['Language']>) => {
            if (state.data?.Settings) {
                state.language = payload;
                state.data.Settings.Language = payload;
            }
        }),

        setUserDataApplicationModes: create.reducer(
            (state, { payload }: PayloadAction<Pick<UserSettings, 'ViewMode' | 'Appearance'>>) => {
                state.applicationModes = payload;
            },
        ),
    }),
    // Every logout (sign out, session expiration 401, failed user info request) discards the user data and sets
    // `isLoading` back to `true`, otherwise MainApp skips the user info request after the next sign in and
    // shows its loader forever
    extraReducers: (builder) => {
        builder.addCase(doLogout, () => initialState);
    },
    selectors: {
        selectIsUserDataLoading: (userData) => userData.isLoading,
        selectUserData: (userData) => userData.data,
        selectUserId: (userData) => userData.data?.UserId,
        selectUserPermissionsList: (userData) => userData.data?.PermissionList,
        selectUserSettings: (userData) => userData.data?.Settings,
        selectUserSettingsCurrencyId: (userData) => userData.data?.Settings?.CurrencyId,

        selectUserLanguage: (userData) => userData.language,

        selectUserAppearance: (userData) => userData.applicationModes.Appearance,
        selectUserViewMode: (userData) => userData.applicationModes.ViewMode,
        selectUserApplicationModes: (userData) => userData.applicationModes,

        selectUserActivePartner: (userData) => userData.data?.ActivePartner,
        selectUserActivePartnerId: (userData) => userData.data?.ActivePartner.PartnerId,
        selectUserActivePartnerCurrencyId: (userData) => userData.data?.ActivePartner.CurrencyId,
        selectUserActivePartnerTimeZone: (userData) => userData.data?.ActivePartner.TimeZone,
    },
});

export const {
    setUserDataFetch,
    setUserData,
    setUserDataFailed,

    setUserDataLanguage,
    setUserDataApplicationModes,
} = userDataSlice.actions;
export const {
    selectIsUserDataLoading,
    selectUserData,
    selectUserPermissionsList,
    selectUserSettings,
    selectUserSettingsCurrencyId,
    selectUserId,

    selectUserLanguage,

    selectUserAppearance,
    selectUserViewMode,
    selectUserApplicationModes,

    selectUserActivePartner,
    selectUserActivePartnerId,
    selectUserActivePartnerCurrencyId,
    selectUserActivePartnerTimeZone,
} = userDataSlice.selectors;
